// The structured event taxonomy (docs/10 §2). A log line keeps its
// human-readable `msg` for the on-screen log and gains one of these as its
// analysable payload; only the payload is ever uploaded, so the free text can
// stay as expressive as a developer wants without becoming an exfiltration
// path.
//
// Every constructor below is TOTAL and LOSSY BY DESIGN: it takes whatever the
// call site has and returns a value whose every field is a number, an enum
// member from a closed set, or a host that went through `safeHost`. There is no
// pass-through string field anywhere in this file, which is what makes "a token
// cannot appear in a report" a structural property rather than a promise.
//
// The taxonomy is driven by what the 2026-07-26 field incident actually lacked:
// which endpoint the race won (never logged at all), whether REST was slow or
// blocked, and how many bytes a socket had carried before it went silent.

import {
  count,
  pathTemplate,
  pick,
  safeHost,
  safeHostFromUrl,
} from './redact.js';

/** How a connection attempt to one endpoint ended. */
export const ENDPOINT_OUTCOMES = ['open', 'timeout', 'refused', 'tls', 'closed', 'other'] as const;
export type EndpointOutcome = (typeof ENDPOINT_OUTCOMES)[number];

/** Endpoint-list `kind` (protocol/endpoints.ts). Carried because "we are down to
 *  the IP tier" is a diagnosis and the address itself is not. */
export const ENDPOINT_KINDS = ['domain', 'ip'] as const;
export type EndpointKind = (typeof ENDPOINT_KINDS)[number];

/** Methods the REST surface uses. */
export const HTTP_METHODS = ['GET', 'POST', 'PATCH', 'PUT', 'DELETE', 'HEAD'] as const;
export type HttpMethod = (typeof HTTP_METHODS)[number];

/**
 * How an HTTP attempt ended. `frozen` is the one that matters most: Russian
 * carriers freeze foreign-datacenter TLS mid-flight with no RST (docs/04 §1),
 * which is a headers-then-silence shape, not a timeout at connect.
 */
export const HTTP_OUTCOMES = [
  'ok',
  'status',
  'timeout',
  'rejected',
  'frozen',
  'cancelled',
  'other',
] as const;
export type HttpOutcome = (typeof HTTP_OUTCOMES)[number];

/** Status CLASSES. `status_401` would be a code token too, but the class is
 *  what a rollup groups by and the code adds nothing a path does not. */
export const HTTP_STATUS_OUTCOMES = [
  'status_1xx',
  'status_2xx',
  'status_3xx',
  'status_4xx',
  'status_5xx',
] as const;
export type HttpStatusOutcome = (typeof HTTP_STATUS_OUTCOMES)[number];

/** The status class of an HTTP response, as an outcome code. */
export function statusOutcome(status: number): HttpStatusOutcome | 'other' {
  const klass = Math.floor(status / 100);
  return klass >= 1 && klass <= 5 ? (`status_${klass}xx` as HttpStatusOutcome) : 'other';
}

/** What asked for a token. `expiry` vs `unauthorized` is the 15-minute fuse. */
export const REFRESH_TRIGGERS = ['boot', 'request', 'expiry', 'unauthorized', 'other'] as const;
export type RefreshTrigger = (typeof REFRESH_TRIGGERS)[number];

/** Coarse network kind — an OS-owned fact the host supplies (docs/10 §1). */
export const NETWORK_KINDS = ['wifi', 'cellular', 'ethernet', 'other', 'none', 'unknown'] as const;
export type NetworkKind = (typeof NETWORK_KINDS)[number];

/** Furthest call phase reached — `call/types.ts` CallPhase, verbatim. */
export const CALL_PHASES = [
  'idle',
  'dialing',
  'ringing_remote',
  'ringing',
  'connecting',
  'in_call',
  'reconnecting',
] as const;
export type CallPhaseReached = (typeof CALL_PHASES)[number];

/** ICE candidate TYPE, never a candidate — an address is a media payload (§3). */
export const ICE_PATHS = ['host', 'srflx', 'relay'] as const;
export type IcePath = (typeof ICE_PATHS)[number];

/** `call/types.ts` CallEndCause, verbatim, plus a total fallback. */
export const CALL_END_REASONS = [
  'hangup',
  'declined',
  'busy',
  'no_answer',
  'missed',
  'answered_elsewhere',
  'connection_lost',
  'failed',
  'mic_denied',
  'security',
  'other',
] as const;
export type CallEndReason = (typeof CALL_END_REASONS)[number];

export interface EndpointAttemptEvent {
  event: 'endpoint.attempt';
  host: string;
  kind: EndpointKind;
  outcome: EndpointOutcome;
  elapsedMs: number;
}

/** The fact we did not have on 2026-07-26 (docs/10 §2). */
export interface EndpointWonEvent {
  event: 'endpoint.won';
  host: string;
  round: number;
  elapsedMs: number;
}

export interface HttpAttemptEvent {
  event: 'http.attempt';
  host: string;
  path: string;
  method: HttpMethod;
  /** 1-based; the intake rejects 0. */
  attempt: number;
  deadlineMs: number;
  elapsedMs: number;
  /**
   * A transport kind, or `status_4xx`/`status_5xx` for an answer that arrived.
   * The CLASS is a diagnosis; the exact code is one too many degrees of freedom
   * for a field whose vocabulary must stay a closed set of code tokens.
   */
  outcome: HttpOutcome | HttpStatusOutcome;
}

/**
 * The byte counters are the point of this event. If field sockets consistently
 * die near 15–20 KB the counter proves the documented freeze; if they die at
 * wildly different volumes it refutes it. Neither is knowable today.
 */
export interface WsLifetimeEvent {
  event: 'ws.lifetime';
  host: string;
  aliveMs: number;
  framesIn: number;
  framesOut: number;
  bytesIn: number;
  bytesOut: number;
  /** 0 when the socket ended without one. */
  closeCode: number;
}

export interface TokenRefreshEvent {
  event: 'token.refresh';
  trigger: RefreshTrigger;
  outcome: HttpOutcome;
  elapsedMs: number;
}

export interface NetChangeEvent {
  event: 'net.change';
  from: NetworkKind;
  to: NetworkKind;
}

export interface CallOutcomeEvent {
  event: 'call.outcome';
  /**
   * Furthest phase reached — which is also the honest answer to "did it ever
   * connect", since only a connected call reaches `in_call`.
   */
  phase: CallPhaseReached;
  /** Omitted, not nulled, when the path is unknown: the intake's only optional
   *  payload field, and a null would fail it. */
  icePath?: IcePath;
  reason: CallEndReason;
}

export type DiagEvent =
  | EndpointAttemptEvent
  | EndpointWonEvent
  | HttpAttemptEvent
  | WsLifetimeEvent
  | TokenRefreshEvent
  | NetChangeEvent
  | CallOutcomeEvent;

export type DiagEventName = DiagEvent['event'];

export const DIAG_EVENT_NAMES: readonly DiagEventName[] = [
  'endpoint.attempt',
  'endpoint.won',
  'http.attempt',
  'ws.lifetime',
  'token.refresh',
  'net.change',
  'call.outcome',
];

// ---------------------------------------------------------------------------
// Constructors. Loose in, closed out.
// ---------------------------------------------------------------------------

export function endpointAttempt(input: {
  url?: unknown;
  host?: unknown;
  kind?: unknown;
  outcome?: unknown;
  elapsedMs?: unknown;
}): EndpointAttemptEvent {
  return {
    event: 'endpoint.attempt',
    host: input.url === undefined ? safeHost(input.host) : safeHostFromUrl(input.url),
    kind: pick(input.kind, ENDPOINT_KINDS, 'domain'),
    outcome: pick(input.outcome, ENDPOINT_OUTCOMES, 'other'),
    elapsedMs: count(input.elapsedMs),
  };
}

export function endpointWon(input: {
  url?: unknown;
  host?: unknown;
  round?: unknown;
  elapsedMs?: unknown;
}): EndpointWonEvent {
  return {
    event: 'endpoint.won',
    host: input.url === undefined ? safeHost(input.host) : safeHostFromUrl(input.url),
    round: count(input.round, 64),
    elapsedMs: count(input.elapsedMs),
  };
}

export function httpAttempt(input: {
  url?: unknown;
  host?: unknown;
  path?: unknown;
  method?: unknown;
  attempt?: unknown;
  deadlineMs?: unknown;
  elapsedMs?: unknown;
  outcome?: unknown;
}): HttpAttemptEvent {
  return {
    event: 'http.attempt',
    host: input.url === undefined ? safeHost(input.host) : safeHostFromUrl(input.url),
    path: pathTemplate(input.path ?? input.url),
    method: pick(
      typeof input.method === 'string' ? input.method.toUpperCase() : input.method,
      HTTP_METHODS,
      'GET',
    ),
    // 1-based, and never 0: the intake's floor is 1 because "attempt zero" is
    // not a thing that happened.
    attempt: Math.max(1, count(input.attempt, 32)),
    deadlineMs: count(input.deadlineMs),
    elapsedMs: count(input.elapsedMs),
    outcome: pick(input.outcome, [...HTTP_OUTCOMES, ...HTTP_STATUS_OUTCOMES], 'other'),
  };
}

/**
 * The counters are REQUIRED, here and at the intake, because a `ws.lifetime`
 * without them is the event minus its purpose. "We did not measure" and "it
 * carried nothing" are opposite findings for the freeze hypothesis, so a caller
 * that cannot measure must emit NO EVENT rather than a fabricated zero — see
 * `wsLifetimeIfMeasured`.
 */
export function wsLifetime(input: {
  url?: unknown;
  host?: unknown;
  aliveMs?: unknown;
  framesIn?: unknown;
  framesOut?: unknown;
  bytesIn?: unknown;
  bytesOut?: unknown;
  closeCode?: unknown;
}): WsLifetimeEvent {
  return {
    event: 'ws.lifetime',
    host: input.url === undefined ? safeHost(input.host) : safeHostFromUrl(input.url),
    aliveMs: count(input.aliveMs),
    framesIn: count(input.framesIn),
    framesOut: count(input.framesOut),
    bytesIn: count(input.bytesIn),
    bytesOut: count(input.bytesOut),
    closeCode: count(input.closeCode, 65_535),
  };
}

/** `wsLifetime`, or null when the transport did not expose byte counters. */
export function wsLifetimeIfMeasured(input: {
  url?: unknown;
  host?: unknown;
  aliveMs?: unknown;
  framesIn?: unknown;
  framesOut?: unknown;
  bytesIn?: unknown;
  bytesOut?: unknown;
  closeCode?: unknown;
}): WsLifetimeEvent | null {
  const measured = typeof input.bytesIn === 'number' && typeof input.bytesOut === 'number';
  return measured ? wsLifetime(input) : null;
}

export function tokenRefresh(input: {
  trigger?: unknown;
  outcome?: unknown;
  elapsedMs?: unknown;
}): TokenRefreshEvent {
  return {
    event: 'token.refresh',
    trigger: pick(input.trigger, REFRESH_TRIGGERS, 'other'),
    outcome: pick(input.outcome, HTTP_OUTCOMES, 'other'),
    elapsedMs: count(input.elapsedMs),
  };
}

export function netChange(input: { from?: unknown; to?: unknown }): NetChangeEvent {
  return {
    event: 'net.change',
    from: pick(input.from, NETWORK_KINDS, 'unknown'),
    to: pick(input.to, NETWORK_KINDS, 'unknown'),
  };
}

export function callOutcome(input: {
  phase?: unknown;
  icePath?: unknown;
  reason?: unknown;
}): CallOutcomeEvent {
  const icePath =
    typeof input.icePath === 'string' && (ICE_PATHS as readonly string[]).includes(input.icePath)
      ? (input.icePath as IcePath)
      : undefined;
  const base: CallOutcomeEvent = {
    event: 'call.outcome',
    phase: pick(input.phase, CALL_PHASES, 'idle'),
    reason: pick(input.reason, CALL_END_REASONS, 'other'),
  };
  return icePath === undefined ? base : { ...base, icePath };
}

// ---------------------------------------------------------------------------
// Human rendering — the on-screen log half of docs/10 §2.
// ---------------------------------------------------------------------------

/**
 * The `msg` a structured event gets on the log screen. Built from the ALREADY
 * REDACTED event, never from the raw call-site input, so the two halves of a
 * line can never disagree about what was safe to say.
 */
export function describeEvent(e: DiagEvent): string {
  switch (e.event) {
    case 'endpoint.attempt':
      return `endpoint ${e.outcome}: host=${e.host} kind=${e.kind} ${e.elapsedMs}ms`;
    case 'endpoint.won':
      return `endpoint won: host=${e.host} round=${e.round} ${e.elapsedMs}ms`;
    case 'http.attempt':
      return `http ${e.outcome}: host=${e.host} ${e.method} ${e.path} attempt=${e.attempt} deadline=${e.deadlineMs}ms ${e.elapsedMs}ms`;
    case 'ws.lifetime':
      return `ws closed: host=${e.host} alive=${e.aliveMs}ms frames=${e.framesIn}/${e.framesOut} bytes=${e.bytesIn}/${e.bytesOut} code=${e.closeCode || '-'}`;
    case 'token.refresh':
      return `token refresh ${e.outcome}: trigger=${e.trigger} ${e.elapsedMs}ms`;
    case 'net.change':
      return `network changed: ${e.from} → ${e.to}`;
    case 'call.outcome':
      return `call ended: phase=${e.phase} ice=${e.icePath ?? '-'} reason=${e.reason}`;
  }
}

/** Level a structured event should be logged at. */
export function levelOf(e: DiagEvent): 'info' | 'warn' {
  switch (e.event) {
    case 'endpoint.attempt':
      return e.outcome === 'open' ? 'info' : 'warn';
    case 'http.attempt':
      return e.outcome === 'ok' || e.outcome === 'cancelled' || e.outcome === 'status_2xx'
        ? 'info'
        : 'warn';
    case 'token.refresh':
      return e.outcome === 'ok' ? 'info' : 'warn';
    case 'ws.lifetime':
      return 'warn';
    default:
      return 'info';
  }
}
